import { memo, useCallback, useId, useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import { Eye, EyeOff } from "lucide-react";
import Icon, { IconIdType } from "./Icon";

const supportedCardIcons = [
  "icon-visa_28",
  "icon-amex_28",
  "icon-discover_28",
  "icon-mastercard_28",
];

export type InputProps = {
  label?: string;
  errorMessage?: string;
  variant?: "default" | "card";
} & React.ComponentProps<"input">;

const Input = ({
  className,
  id,
  type = "text",
  label,
  required,
  errorMessage,
  variant = "default",
  ...props
}: InputProps) => {
  const generatedInputId = useId();
  const shouldShowCardIcons = variant === "card";

  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  const memoizedInputType = useMemo(() => {
    if (type === "password") {
      return isPasswordVisible ? "text" : "password";
    }

    return type;
  }, [isPasswordVisible, type]);

  const iconClickHandler = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      setIsPasswordVisible((prev) => !prev);
    },
    [setIsPasswordVisible]
  );

  const fieldAction = useMemo(() => {
    if (type !== "password") {
      return null;
    }

    const title = isPasswordVisible ? "Password shown" : "Password hidden";

    return (
      <button
        type="button"
        onClick={iconClickHandler}
        title={title}
        aria-label={title}
        className="cursor-pointer outline-none inline-flex items-center justify-center select-none touch-manipulation p-3 bg-transparent transition-colors absolute top-1/2 -translate-y-1/2 right-0.5 text-muted-foreground hover:text-semantic-text-primary active:text-semantic-text-primary"
      >
        {isPasswordVisible ? <Eye size={16} /> : <EyeOff size={16} />}
      </button>
    );
  }, [type, variant, iconClickHandler, isPasswordVisible]);

  return (
    <div className="grid grid-cols-1 place-content-start gap-2">
      <div className="grid grid-cols-1 place-content-start gap-2">
        {!!label && (
          <label
            htmlFor={id ?? generatedInputId}
            className={cn(
              "text-semantic-text-primary text-base font-normal leading-[130%]",
              {
                "after:content-['*'] after:ml-2 after:text-error": required,
              }
            )}
          >
            {label}
          </label>
        )}
        <div className="relative w-full">
          <input
            id={id ?? generatedInputId}
            data-slot="input"
            type={memoizedInputType}
            className={cn(
              "rounded-xl bg-white placeholder:text-muted-foreground placeholder:transition-opacity placeholder:opacity-100 placeholder:duration-300 placeholder:text-xs sm:placeholder:text-sm border-input flex w-full min-w-0 border px-3 py-[13.2px] shadow-xs transition-[color,box-shadow] outline-none disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 text-sm focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] focus-visible:placeholder:opacity-0",
              {
                "border-error": !!errorMessage,
                "pr-37": shouldShowCardIcons,
                "pr-12": !!fieldAction,
              },
              className
            )}
            required={required}
            {...props}
          />
          {!!shouldShowCardIcons && (
            <div className="flex items-center absolute gap-1 top-1/2 -translate-y-1/2 right-3">
              {supportedCardIcons.map((iconId, i) => (
                <Icon
                  key={i}
                  id={iconId as IconIdType}
                  width={28}
                  height={28}
                  className="shrink-0"
                />
              ))}
            </div>
          )}
          {fieldAction}
        </div>
      </div>
      {!!errorMessage && (
        <span className="text-error text-xs">{errorMessage}</span>
      )}
    </div>
  );
};

export default memo(Input);

export { Input };
